<template>
  <div class="pay-range-draft-action-bar">
    <div class="pay-range-draft-action-bar__stepper">
      <template v-if="!isVersion">
        <span class="pay-range-draft-action-bar__stepper-step">Step {{ step }} of 2</span>
        <span class="pay-range-draft-action-bar__stepper-dot">·</span>
      </template>
      <span class="pay-range-draft-action-bar__stepper-context" data-test="action-context">{{ context }}</span>
    </div>

    <div class="pay-range-draft-action-bar__actions">
      <DropDownMenu
        v-if="!allActionsDisabled"
        class="pay-range-draft-action-bar__more"
        :dropdown-items="moreActionsItems"
        :is-open="false"
        :width="180"
        tip-side="left"
        :translate-y-offset="8"
        :translate-x-offset="0"
        @onClick="onMoreAction"
      >
        <template #activator>
          <button
            type="button"
            class="prd-more"
            aria-label="More actions"
            aria-haspopup="menu"
            data-test="action-more"
          >
            <v-icon size="20" color="#7f56d9">mdi-dots-vertical</v-icon>
          </button>
        </template>
      </DropDownMenu>
      <button
        v-else
        type="button"
        class="prd-more"
        aria-label="More actions"
        data-test="action-more"
        disabled
      >
        <v-icon size="20" color="#98a2b3">mdi-dots-vertical</v-icon>
      </button>

      <template v-if="step === 2 || isVersion">
        <button
          v-if="!isVersion"
          type="button"
          class="prd-btn prd-btn--secondary"
          data-test="action-back"
          :disabled="allActionsDisabled"
          @click="$emit('back')"
        >
          Back
        </button>

        <template v-if="state === 'sourcing'">
          <button
            type="button"
            class="prd-btn prd-btn--secondary"
            data-test="action-stop-sourcing"
            aria-busy="true"
            @click="$emit('stop-sourcing')"
          >
            Stop sourcing
          </button>
        </template>

        <template v-else>
          <AskIQButton
            v-if="suggestEnabled"
            label="Suggest"
            :variant="isVersion ? 'tertiary' : 'light'"
            compact
            data-test="action-suggest"
            :disabled="allActionsDisabled"
            @click="$emit('suggest')"
          />

          <button
            type="button"
            class="prd-btn prd-btn--secondary"
            data-test="action-save"
            :aria-busy="saving ? 'true' : null"
            :disabled="allActionsDisabled || !amountsOk || saveDisabled"
            @click="$emit('save-draft')"
          >
            {{ saving ? "Saving…" : "Save draft" }}
          </button>

          <button
            type="button"
            class="prd-btn prd-btn--primary"
            data-test="action-publish"
            :aria-busy="isPublishing ? 'true' : null"
            :disabled="allActionsDisabled || !amountsOk || publishDisabled"
            @click="$emit('publish')"
          >
            {{ isPublishing ? "Publishing…" : "Publish" }}
          </button>
        </template>
      </template>

      <template v-else>
        <button
          type="button"
          class="prd-btn prd-btn--primary"
          data-test="action-next"
          :disabled="allActionsDisabled || !identityValid || isInvalid || state === 'duplicate-identity'"
          @click="$emit('next')"
        >
          Next
        </button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import AskIQButton from "@components/AskIQButton/AskIQButton.vue";
import DropDownMenu from "@components/DropDownMenu/DropDownMenu.vue";

const props = defineProps({
  step: { type: Number, default: 1, validator: (v) => v === 1 || v === 2 },
  /** default | invalid | sourcing | publishing, plus the derived drawer states (duplicate-identity | max-must-exceed-min | iq-suggestion | sourcing | cannot-source | timed-out | sourcing-stopped | publishing | publish-failed | save-failed | saved). */
  state: { type: String, default: "default" },
  identityValid: { type: Boolean, default: true },
  amountsValid: { type: Boolean, default: true },
  suggestEnabled: { type: Boolean, default: false },
  publishing: { type: Boolean, default: false },
  /** Extra Publish-only block (No change / Date conflict); Save draft is blocked separately by `saveDisabled`. */
  publishDisabled: { type: Boolean, default: false },
  /** Extra Save-draft-only block: the values equal the current version, so there is nothing to save. */
  saveDisabled: { type: Boolean, default: false },
  /** A save is in flight: every action is disabled so a double-click cannot send a second write. */
  saving: { type: Boolean, default: false },
  /** `wizard` (two steps, Back on Step 2) or `version` (single-step version drawer: no step prefix, no Back; the drawer header × closes). */
  variant: { type: String, default: "wizard", validator: (v) => v === "wizard" || v === "version" },
});

const emit = defineEmits(["discard", "next", "back", "suggest", "save-draft", "publish", "stop-sourcing"]);

const moreActionsItems = [
  { name: "Discard draft…", value: "discard-draft", icon: "icons/dropdown/trash-01.svg", negative: true },
];

function onMoreAction(item) {
  if (item?.value === "discard-draft") emit("discard");
}

const isVersion = computed(() => props.variant === "version");

const isPublishing = computed(() => props.publishing || props.state === "publishing");
const isInvalid = computed(() => props.state === "invalid");
const amountsOk = computed(() => props.amountsValid && !isInvalid.value);
const allActionsDisabled = computed(() => isPublishing.value || props.saving);

const context = computed(() => {
  if (isVersion.value) {
    if (props.state === "sourcing") return "IQ is sourcing market data.";
    if (isPublishing.value) return "Publishing…";
    if (props.state === "max-must-exceed-min") return "Fix the errors to save or publish.";
    if (props.publishDisabled) return "Fix the errors to publish.";
    return "Draft · not published. No position uses it until you publish.";
  }
  if (props.step === 1) {
    if (props.state === "duplicate-identity") {
      return "Identity · This identity already exists. Change it to continue.";
    }
    return isInvalid.value
      ? "Identity · Complete the required fields to continue."
      : "Identity · Draft, not published.";
  }
  if (props.state === "sourcing") return "Amounts · IQ is sourcing market data.";
  if (isPublishing.value) return "Amounts · Publishing…";
  if (isInvalid.value || props.state === "max-must-exceed-min") return "Amounts · Fix the errors to save or publish.";
  return "Amounts · Draft, not published. No position uses it until you publish.";
});
</script>

<style scoped>
.pay-range-draft-action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 12px 24px;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
  flex-wrap: wrap;
}
.pay-range-draft-action-bar__stepper {
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.pay-range-draft-action-bar__stepper-step {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}
.pay-range-draft-action-bar__stepper-dot {
  color: #98a2b3;
}
.pay-range-draft-action-bar__stepper-context {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}
.pay-range-draft-action-bar__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
.prd-btn {
  border-radius: 8px;
  padding: 10px 14px;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  border: 1px solid transparent;
}
.prd-btn:disabled {
  cursor: not-allowed;
}
.prd-btn--primary {
  background-color: #7f56d9;
  color: #ffffff;
  border-color: #7f56d9;
}
.prd-btn--primary:disabled {
  background-color: #f2f4f7;
  color: #98a2b3;
  border-color: #eaecf0;
}
.prd-btn--secondary {
  background-color: #ffffff;
  color: #344054;
  border-color: #d0d5dd;
}
.prd-btn--secondary:disabled {
  background-color: #ffffff;
  color: #98a2b3;
  border-color: #eaecf0;
}
.prd-more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 8px;
  background-color: #f2edff;
  cursor: pointer;
}
.prd-more:disabled {
  background-color: #f2f4f7;
  cursor: not-allowed;
}
.prd-more:focus-visible,
.prd-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}
</style>
